import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * VariantSelector
 * Renders one selector group per variant attribute key (e.g. color, storage, version).
 * When the user picks a value, finds the best-matching sibling variant and navigates to it.
 *
 * Props:
 *   currentProduct: the active product
 *   variants: array of sibling products in the same group (includes current)
 */
const VariantSelector = ({ currentProduct, variants }) => {
  const navigate = useNavigate();

  // Build a map: attrKey -> [unique values in stable order]
  const attributeOptions = useMemo(() => {
    if (!variants || variants.length <= 1) return {};
    const map = {};
    for (const v of variants) {
      const attrs = v.variant_attributes || {};
      for (const [k, val] of Object.entries(attrs)) {
        if (!val) continue;
        if (!map[k]) map[k] = [];
        if (!map[k].includes(val)) map[k].push(val);
      }
    }
    return map;
  }, [variants]);

  const currentAttrs = currentProduct?.variant_attributes || {};

  // Given a key+value, return the variant the user would land on (or current product)
  const findTarget = (key, value) => {
    if (currentAttrs[key] === value) return currentProduct;
    const desired = { ...currentAttrs, [key]: value };
    const scored = variants
      .filter((v) => v.id !== currentProduct.id)
      .map((v) => {
        const va = v.variant_attributes || {};
        if (va[key] !== value) return null;
        let score = 0;
        for (const [k, val] of Object.entries(desired)) {
          if (k === key) continue;
          if (va[k] === val) score++;
        }
        return { variant: v, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score);
    return scored.length > 0 ? scored[0].variant : null;
  };

  // Pick the best variant when user clicks a new value for a key
  const handleSelect = (key, value) => {
    if (currentAttrs[key] === value) return;
    const target = findTarget(key, value);
    if (target && target.stock > 0) {
      navigate(`/product/${target.id}`);
    }
  };

  // Only show attribute keys that are assigned to the CURRENT product.
  // Sibling-only attributes (e.g. "Console Edition" on PS5 when viewing PS5 Pro) are hidden.
  const keys = Object.keys(attributeOptions).filter((k) => currentAttrs[k] !== undefined && currentAttrs[k] !== '');
  if (keys.length === 0) return null;

  // Helpers for visual rendering of color swatches
  const isColorKey = (k) => /(colou?r|shade|finish)/i.test(k);
  const colorMap = {
    black: '#0f172a', white: '#f8fafc', red: '#ef4444', blue: '#3b82f6',
    green: '#10b981', yellow: '#eab308', purple: '#8b5cf6', pink: '#ec4899',
    gold: '#d4af37', silver: '#cbd5e1', gray: '#64748b', grey: '#64748b',
    orange: '#f97316', brown: '#78350f', beige: '#e7d3b3', navy: '#1e3a8a',
    titanium: '#7e8895', graphite: '#374151', cream: '#fef3c7', rose: '#fda4af',
    bronze: '#cd7f32', copper: '#b87333', mint: '#a7f3d0', teal: '#14b8a6',
    cyan: '#06b6d4', lime: '#a3e635', maroon: '#7f1d1d', khaki: '#bdb76b',
    ivory: '#fffff0', sand: '#e7d3b3', coral: '#ff7f50', lavender: '#c4b5fd',
    charcoal: '#1f2937', midnight: '#0c1e3e', sky: '#7dd3fc',
  };

  // Fuzzy match: "Soft Pink" → matches "pink"; "Midnight Black" → matches "midnight" first
  // then "black". Returns the FIRST matching keyword's hex, or null if no match.
  const colorForName = (name) => {
    const n = (name || '').toLowerCase().trim();
    if (!n) return null;
    // Direct exact match first
    if (colorMap[n]) return colorMap[n];
    // Find any keyword from colorMap that appears in the name
    for (const key of Object.keys(colorMap)) {
      // Use word-boundary check to avoid e.g. "graphite" matching "graphite gray"
      const re = new RegExp(`\\b${key}\\b`, 'i');
      if (re.test(n)) return colorMap[key];
    }
    return null;
  };

  return (
    <div className="space-y-5 mb-5" data-testid="variant-selector">
      {keys.map((key) => {
        const values = attributeOptions[key];
        const selected = currentAttrs[key];
        const colorish = isColorKey(key);

        // For color keys: build a lookup from value → first variant that has it (use its image)
        const variantByValue = {};
        if (colorish) {
          for (const v of variants) {
            const va = v.variant_attributes || {};
            if (va[key] && !variantByValue[va[key]]) {
              variantByValue[va[key]] = v;
            }
          }
        }

        return (
          <div key={key}>
            <div className="mb-2.5">
              <span className="text-sm text-slate-600 capitalize">{key.replace(/_/g, ' ')}: </span>
              {selected && (
                <span className="text-sm font-semibold text-slate-900" data-testid={`selected-${key}`}>{selected}</span>
              )}
            </div>

            {colorish ? (
              /* Noon-style image cards for color variants */
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {values.map((val) => {
                  const isActive = selected === val;
                  const target = findTarget(key, val);
                  const outOfStock = !target || target.stock <= 0;
                  const matchVariant = variantByValue[val];
                  const imgSrc = matchVariant?.image_url;
                  return (
                    <button
                      key={val}
                      type="button"
                      onClick={() => handleSelect(key, val)}
                      disabled={outOfStock && !isActive}
                      title={outOfStock ? 'Out of stock' : val}
                      className={`relative w-[88px] sm:w-[104px] rounded-xl border-2 transition-all bg-white flex flex-col items-center justify-end overflow-hidden ${
                        isActive
                          ? 'border-[#0047AB] ring-1 ring-[#0047AB]/30 shadow-sm'
                          : outOfStock
                            ? 'border-slate-200 opacity-60 cursor-not-allowed'
                            : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`variant-option-${key}-${val}`}
                    >
                      {/* Image preview area */}
                      <div className={`w-full h-20 sm:h-24 flex items-center justify-center p-1.5 ${outOfStock ? 'grayscale' : ''}`}>
                        {imgSrc ? (
                          <img
                            src={imgSrc}
                            alt={val}
                            className="max-w-full max-h-full object-contain"
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                              if (!e.target.dataset.fallback) {
                                e.target.dataset.fallback = '1';
                                const swatch = colorForName(val);
                                e.target.style.display = 'none';
                                const ph = e.target.nextElementSibling;
                                if (ph) ph.style.display = 'block';
                                ph?.setAttribute('style', `display:block; background:${swatch || '#cbd5e1'};`);
                              }
                            }}
                          />
                        ) : null}
                        {/* Color fallback shown when no image */}
                        <div
                          className="rounded-full w-14 h-14"
                          style={{ display: imgSrc ? 'none' : 'block', background: colorForName(val) || 'linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)' }}
                        />
                      </div>
                      {/* Name label */}
                      <div className={`w-full text-center px-2 py-1.5 text-xs font-medium ${isActive ? 'text-slate-900' : outOfStock ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                        {val}
                      </div>
                      {outOfStock && (
                        <div className="w-full text-center pb-1 -mt-1 text-[9px] font-semibold uppercase tracking-wide text-red-500" data-testid={`variant-oos-${key}-${val}`}>
                          Out of stock
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              /* Pill style for non-color attributes */
              <div className="flex flex-wrap gap-2">
                {values.map((val) => {
                  const isActive = selected === val;
                  const target = findTarget(key, val);
                  const outOfStock = !target || target.stock <= 0;
                  return (
                    <button
                      key={val}
                      type="button"
                      onClick={() => handleSelect(key, val)}
                      disabled={outOfStock && !isActive}
                      title={outOfStock ? 'Out of stock' : val}
                      className={`relative px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                        isActive
                          ? 'border-[#0047AB] bg-[#0047AB]/5 text-[#0047AB]'
                          : outOfStock
                            ? 'border-slate-200 bg-slate-50 text-slate-400 line-through cursor-not-allowed overflow-hidden'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                      }`}
                      data-testid={`variant-option-${key}-${val}`}
                    >
                      {val}
                      {outOfStock && !isActive && (
                        <span className="absolute inset-0 pointer-events-none" aria-hidden="true">
                          <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100">
                            <line x1="0" y1="100" x2="100" y2="0" stroke="#cbd5e1" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
                          </svg>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default VariantSelector;
